import { getSessionStorage, setSessionStorage } from "./sessionStorageHelpers";

export interface TitleChangeAssign extends Document {
  titleChangeHandler: ReturnType<typeof setTimeout>;
}

export interface TitleChangeDelete extends Document {
  titleChangeHandler?: ReturnType<typeof setTimeout>;
}

/*Helper to get AccordionState*/
export const getAccordionState = (
  session: string,
  account: string,
  accordionName: string
) => {
  const accordionState = getSessionStorage(session + account);
  if (!accordionState || accordionState[accordionName] === undefined)
    return false;
  return accordionState[accordionName];
};

export const checkSavedSessionHandler = (session: string) => {
  const { sessionStorage } = window;
  if (!sessionStorage.length) return true;

  let isSaved = true;
  Object.keys(sessionStorage).forEach((key) => {
    if (key.includes(session) && key.length > session.length) {
      const options = JSON.parse(sessionStorage[key]);
      if (options.SAVED !== undefined) isSaved = isSaved && options.SAVED;
    }
  });

  return isSaved;
};

/*Helper to set and switch accordionState*/

export const setAccordionState = (
  session: string,
  account: string,
  accordionName: string
) => {
  const uniqueKey = session + account;

  const isPresent = getSessionStorage(uniqueKey);

  if (!isPresent) {
    const newItem = {
      [accordionName]: true,
    };
    setSessionStorage(uniqueKey, newItem);
    return;
  }

  const accordionState = {
    ...isPresent,
    [accordionName]: !isPresent[accordionName],
  };
  setSessionStorage(uniqueKey, accordionState);
};

/*Helper to set and switch right panel state*/

export const setRightPanelState = (
  sessionKey: string,
  currentState: boolean
) => {
  const state = { isHidden: currentState };
  setSessionStorage(sessionKey, state);
};

/*Helper to get right panel state*/

export const getRightPanelState = (sessionKey: string) => {
  return getSessionStorage(sessionKey);
};

/*Constants and helpers to work with share session modal*/

const headerHeight = 64;
const modalMargin = 16;

export const scrollModalCoordinatesHandler = (
  buttonCenterFromTop: number,
  numberOfUsersToShare: number
): { [key: string]: number } => {
  const screenHeight = window.innerHeight;
  const modalHeaderHeight = 48;
  const modalFooterHeight = 64;
  const modalItemHeight = 48;
  const contentHeightDelta = 3;

  const contentHeight =
    numberOfUsersToShare <= 3
      ? 144
      : numberOfUsersToShare * modalItemHeight + contentHeightDelta;

  const fullModalHeight = contentHeight + modalHeaderHeight + modalFooterHeight;
  const maxModalHeight = scrollModalMaxHeightHandler();

  const modalHeight =
    fullModalHeight >= maxModalHeight ? maxModalHeight : fullModalHeight;

  const modalTop = buttonCenterFromTop - modalHeight / 2;
  const modalBottom = modalTop + modalHeight + modalMargin;

  const topUpperPosition = headerHeight + modalMargin;
  const topBottomPosition = screenHeight - modalHeight - modalMargin;

  if (modalTop < topUpperPosition) {
    return {
      topPosition: topUpperPosition,
      modalHeight,
      contentHeight,
    };
  }

  if (modalBottom > screenHeight) {
    return { topPosition: topBottomPosition, modalHeight, contentHeight };
  }

  return { topPosition: modalTop, modalHeight, contentHeight };
};

export const scrollModalMaxHeightHandler = (): number => {
  const height = window.innerHeight - headerHeight - 2 * modalMargin;
  return height;
};

export const yesNoCellIcon = ({ value }: { value: string }) => {
  if (typeof value === "boolean") {
    return `<cas-icon class="a-cellIcon-${value}" name="${
      value ? "checkbox" : "cross"
    }"></cas-icon>`;
  }
  return "";
};
export const yesNoFilterIcon = ({ value }: { value: string }) => {
  if (typeof value !== "string") {
    return undefined;
  }

  switch (value) {
    case "true":
      return `<cas-icon class="a-filterIcon-true" name="checkbox"></cas-icon>`;
    case "false":
      return `<cas-icon class="a-filterIcon-false" name="cross"></cas-icon>`;
    case "(Select All)":
      return "(Select All)";
    default:
      return "";
  }
};

// TITLE CHANGE FUNCTIONS

export const initTitleChangeHandler = (sessionCount: number) => {
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon && favicon.getAttribute("href") !== "/favicon_message.ico")
    favicon.setAttribute("href", "/favicon_message.ico");

  if ((document as TitleChangeAssign).titleChangeHandler === undefined) {
    document.title = `(${sessionCount}) New Shared Session${
      sessionCount > 1 ? "s" : ""
    }`;

    (document as TitleChangeAssign).titleChangeHandler = setInterval(
      function () {
        document.title.includes("Caspio CloudDesk")
          ? (document.title = `(${sessionCount}) New Shared Session${
              sessionCount > 1 ? "s" : ""
            }`)
          : (document.title = `(${sessionCount}) Caspio CloudDesk`);
      },
      1500
    );
  }
};

export const clearTitleChangeHandler = () => {
  clearInterval((document as TitleChangeAssign).titleChangeHandler);
  delete (document as TitleChangeDelete).titleChangeHandler;

  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon) favicon.setAttribute("href", "/favicon.ico");
  document.title = "Caspio CloudDesk";
};

export const sharedSessionTitleHandler = (sharedSessionsCount: number) => {
  if (sharedSessionsCount > 0) {
    clearInterval((document as TitleChangeAssign).titleChangeHandler);
    delete (document as TitleChangeDelete).titleChangeHandler;
    initTitleChangeHandler(sharedSessionsCount);
  } else {
    if (document.title !== "Caspio CloudDesk") clearTitleChangeHandler();
  }
};

export const openInNewTabHandler = (url: string) => {
  const win = window.open(url, "_blank");
  win && win.focus();
};
